import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { print } from 'graphql';
import { ClientError, GraphQLClient } from 'graphql-request';

import { env } from '@/constants/env';

import { getAuth0Client } from '../auth';
import requestGraphQL from '../graphql/requestGraphQL';
import { authenticateWithAuth0TokenDocument } from './shop/auth/graphql';

const VENDURE_AUTH_TOKEN_STORAGE_KEY = 'vendureAuthToken';

// Add this at the top level to track ongoing authentication
let authenticationPromise: Promise<string | null> | null = null;

export interface RequestOmsApiArgs<Result, Variables> {
  pathName: 'oms-admin-api' | 'shop-api';
  document: TypedDocumentNode<Result, Variables>;
  variables?: Variables;
  headers?: HeadersInit;
}

export default async function requestOmsApi<Result, Variables extends object | undefined>({
  pathName,
  document,
  variables,
  headers,
}: RequestOmsApiArgs<Result, Variables>): Promise<Result> {
  if (document.definitions.length === 0) {
    throw new Error('The document is empty');
  }

  if (document.definitions[0].kind !== 'OperationDefinition' || !document.definitions[0].name) {
    throw new Error('The document must contain a named operation.');
  }

  const operationName = document.definitions[0].name?.value;

  // Including the operation name in the URL is solely to aid debugging; otherwise, we must look at the
  // request body's query document to differentiate between requests in logs and in the browser network tab.
  const baseUrl = `${env.VITE_OMS_SERVICE_API_URL}/${pathName}`;
  const url = `${baseUrl}?opName=${operationName}`;

  let retries = 0;

  // We don't know when the session times out without a trip to the server, so we need to retry the request
  // if we get a FORBIDDEN error.  This code is similar to the code in the Vendure AdminUI here
  // https://github.com/vendure-ecommerce/vendure/blob/4f518d3b60d880369a9cbfdcca6475d77e362d1f/packages/admin-ui/src/lib/core/src/data/providers/interceptor.ts#L86
  while (retries <= 1) {
    // the reason we send two tokens is because each one has a specific purpose
    // accessToken is the vendure access token that associates the request with the user's session
    // the auth0 token is used to facilitate when a user is hijacked and getting the true user id
    // for this specific request.
    const accessToken = await getVendureAuthToken({ url: baseUrl });
    const auth0Token = await getAuth0Client()?.getTokenSilently();

    try {
      const response = await requestGraphQL({
        url,
        document,
        variables,
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
          ...(auth0Token ? { 'X-Auth0-Token': `${auth0Token}` } : {}),
          ...headers,
        },
      });

      return response;
    } catch (error) {
      // graphql-request wraps the errors nicely for us so we can check if it's a ClientError
      if (!(error instanceof ClientError)) {
        throw error;
      }

      // The FORBIDDEN error is wrapped in the response.errors array and should be the first error,
      // so if it's not, throw the error because it's unexpected
      const graphQLErrors = error.response.errors;
      if (!graphQLErrors || !Array.isArray(graphQLErrors) || graphQLErrors[0]?.extensions?.code !== 'FORBIDDEN') {
        throw error;
      }

      // If we've already retried after reauthenticating, but we have an error, so throw that so we can log it
      if (retries === 1) {
        throw error;
      }

      // Clear the expired token so the request will reauth on subsequent attempt
      clearVendureToken();

      retries++;
    }
  }

  // The only way we could really get here is to get an empty response object which should never happen due to error handling above
  throw new Error('Failed to get a response from the OMS API but no error was thrown');
}

async function getVendureAuthToken({ url }: { url: string }) {
  // If we've already stored the access token, use it
  const storedAccessToken: string | null | undefined = localStorage.getItem(VENDURE_AUTH_TOKEN_STORAGE_KEY);
  if (storedAccessToken) {
    return storedAccessToken;
  }

  // If there's already an authentication in progress, wait for it
  if (authenticationPromise) {
    return authenticationPromise;
  }

  // Start a new authentication process
  authenticationPromise = performAuthentication(url);

  try {
    const result = await authenticationPromise;
    return result;
  } finally {
    // Clear the promise so future calls can start fresh
    authenticationPromise = null;
  }
}

async function performAuthentication(url: string): Promise<string | null> {
  // If we don't have an access token, we first have to get the user's Auth0 token
  const auth0Token = await getAuth0Client()?.getTokenSilently();

  // If the user is not logged in, we can't get a Vendure token anonymously
  if (!auth0Token) {
    return null;
  }

  const graphqlRequestClient = new GraphQLClient(`${url}?opName=authenticate`, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  try {
    // Make a call to the authenticate mutation to get a Vendure auth token using the user's Auth0 token
    // Have to use rawRequest here because the requestGraphQL function doesn't support getting the response headers
    const response = await graphqlRequestClient.rawRequest(print(authenticateWithAuth0TokenDocument), {
      auth0Token,
    });

    const vendureAuthToken = response.headers.get('vendure-auth-token');

    if (!vendureAuthToken) {
      return null;
    }

    // Save this off so we don't have to make this call again
    localStorage.setItem(VENDURE_AUTH_TOKEN_STORAGE_KEY, vendureAuthToken);
    return vendureAuthToken;
  } catch (error) {
    return null;
  }
}

export function clearVendureToken() {
  localStorage.removeItem(VENDURE_AUTH_TOKEN_STORAGE_KEY);
}
